import {
  type Price as DreamPrice,
  PriceInterval as DreamPriceInterval,
  type Tier as DreamTier,
  TierStatus,
  type TPricingElement,
  type WebTheme,
} from '@shared/dream-components';

import {
  type Price as SwarmPrice,
  PriceInterval as SwarmPriceInterval,
  type Tier as SwarmTier,
} from '~/interfaces/subscriptions';

interface Params {
  id: string;
  tiers: SwarmTier[];
  theme: WebTheme;
}

const INTERVAL_BY_VALUE: Record<SwarmPriceInterval, DreamPriceInterval> = {
  [SwarmPriceInterval.MONTH]: DreamPriceInterval.MONTH,
  [SwarmPriceInterval.QUARTER]: DreamPriceInterval.QUARTER,
  [SwarmPriceInterval.YEAR]: DreamPriceInterval.YEAR,
  [SwarmPriceInterval.ONE_TIME]: DreamPriceInterval.ONE_TIME,
  [SwarmPriceInterval.DONATION]: DreamPriceInterval.DONATION,
};

const toDreamInterval = (interval: SwarmPriceInterval): DreamPriceInterval => INTERVAL_BY_VALUE[interval];

const toDreamPrice = (price: SwarmPrice): DreamPrice => ({
  id: price.id,
  amount_cents: price.amount_cents,
  original_amount_cents: price.original_amount_cents,
  currency: price.currency,
  interval: toDreamInterval(price.interval),
  cta: price.cta,
  denominator: price.denominator,
  features: price.features ?? [],
  premium_offer: price.premium_offer,
});

const toDreamTier = (tier: SwarmTier, publicationId: string): DreamTier => ({
  id: tier.id,
  publication_id: publicationId,
  name: tier.name,
  description: tier.description,
  prices: tier.prices.map(toDreamPrice),
  status: TierStatus.ACTIVE,
  redirect_url: tier.redirect_url,
  group_enabled: tier.group_enabled,
  group_minimum_quantity: tier.group_minimum_quantity,
  group_discount_percent: tier.group_discount_percent,
  enabled: true,
  discord_access: false,
});

/**
 * Builds a static, non-editable `TPricingElement` for standalone pricing
 * surfaces (e.g. the Spotify Open Access landing page, the community upgrade
 * modal) where tiers/prices are already fetched and filtered by the caller,
 * so `hasFakeData` is set: the `Pricing` wrapper renders this node directly
 * instead of re-fetching and overlaying prices.
 *
 * Only theme-derived accent colors are set; every other style falls back to
 * the `PricingElement` defaults so the cards match builder-created pricing
 * blocks.
 */
export function buildStandalonePricingNode({ id, tiers, theme }: Params): TPricingElement {
  const publicationId = theme.owner_id;

  const ACCENT = '#111827';
  const ACCENT_TEXT = '#ffffff';
  const NEUTRAL_BORDER = '#E5E7EB';
  const RADIUS = '8px';
  const HEADING_COLOR = '#111827';
  const BODY_COLOR = '#374151';
  const MUTED_COLOR = '#6B7280';

  return {
    type: 'pricing',
    attrs: {
      id,
      content: [{ id: `${id}-content`, text: '' }],
      hasFakeData: true,
      internalCheckout: true,
      collectBillingAddress: false,
      gap: 24,

      data: tiers.map((tier) => toDreamTier(tier, publicationId)),

      giftTiersData: [],
      isGiftingEnabled: false,
      isGiftingOnly: false,
      giftFormHeadingTextColor: '#111827',
      giftFormHeadingFontSize: '20px',
      giftTabHeadingTextColor: '#111827',
      giftTabHeadingFontSize: '20px',

      groupTiersData: [],
      isGroupSubscriptionsEnabled: false,
      groupSubsTabHeadingTextColor: '#111827',
      groupSubsTabHeadingFontSize: '20px',
      groupSubsFormSummaryTextColor: '#4b5563',
      groupSubsFormSummaryFontSize: '14px',

      // Card
      cardBackgroundColor: '#ffffff',
      cardPadding: '24px',
      cardShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      cardBorderColor: NEUTRAL_BORDER,
      cardBorderWidth: '1px',
      cardBorderStyle: 'solid',
      cardBorderRadius: RADIUS,
      // Selected card: slightly darker border.
      cardSelectedBorderColor: '#9CA3AF',

      // Text
      nameColor: HEADING_COLOR,
      descriptionColor: MUTED_COLOR,
      featureColor: BODY_COLOR,
      priceColor: HEADING_COLOR,
      priceDenominatorColor: MUTED_COLOR,
      featureIconColor: ACCENT,
      featureSelectedIconColor: ACCENT,

      // Button
      buttonColor: ACCENT,
      buttonTextColor: ACCENT_TEXT,
      buttonBorderRadius: RADIUS,

      // Recommended tier
      recommendedBackgroundColor: ACCENT,
      recommendedTextColor: ACCENT_TEXT,

      // Interval toggle (month/year) — segmented control: gray track, white
      // selected pill (shadow added by the component), dark selected label.
      tabBackgroundColor: '#F3F4F6',
      tabsContainerBorderRadius: RADIUS,
      tabSelectedBackgroundColor: '#ffffff',
      tabTextColor: MUTED_COLOR,
      tabSelectedTextColor: HEADING_COLOR,
      tabBorderRadius: '6px',

      // Checkout form inputs
      inputTextColor: BODY_COLOR,
      inputBorderColor: NEUTRAL_BORDER,
      inputBorderWidth: '1px',
      inputBorderStyle: 'solid',
      inputBorderRadius: RADIUS,
      formLabelTextColor: BODY_COLOR,
    },
  };
}
